<template>
    <div>
        <div
            class="d-flex flex-row align-center justify-space-between"
            :class="{ 'menu-open': menuOpen }"
        >
            <div class="d-flex flex-row align-center">
                <IdentifierAvatar :identifier="userName" :text="avatarText" :size="30" />
                <div class="d-flex justify-center pl-2 flex-column overflow-hidden">
                    <div class="user_name font-weight-bold">
                        {{ userName }}
                        <v-chip
                            v-if="showAdminMode"
                            label
                            size="x-small"
                            color="error"
                            style="height: 15px"
                        >
                            <ShieldCheckIcon size="14" class="mr-1" />
                            Admin
                        </v-chip>
                    </div>
                    <div class="email pe-4">
                        {{ userMail }}
                    </div>
                </div>
            </div>
            <v-icon size="small" color="grey-darken-1">mdi-unfold-more-horizontal</v-icon>
        </div>

        <v-menu
            :activator="activatorId"
            :attach="activatorId"
            offset="5"
            @update:model-value="handleMenuChange"
        >
            <v-list nav density="compact" rounded class="shadow-md">
                <v-list-item
                    v-for="item in menuItems"
                    :key="item.title"
                    :to="item.url"
                    :disabled="item.active === false"
                    link
                    density="compact"
                    @click="handleItemClick(item)"
                >
                    <div class="d-flex flex-row align-center">
                        <component :is="item.icon" class="mr-2" :size="18" />
                        <v-list-item-title>
                            {{ $t(`navigation.${item.title}`) }}
                        </v-list-item-title>
                    </div>
                </v-list-item>
            </v-list>
        </v-menu>

        <UserSettings :visible="showSettingsDialog" @close="handleSettingsClose" />
    </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import _ from 'lodash';
import IdentifierAvatar from '@/components/utils/bits/IdentifierAvatar.vue';
import { useUserStore } from '@/stores/user';
import { ShieldCheckIcon, Settings2Icon, LogOutIcon } from 'lucide-vue-next';
import UserSettings from '@/components/user/settings/UserSettings.vue';

const props = defineProps({
    user: {
        type: Object,
        required: true,
    },
    showAdminMode: {
        type: Boolean,
        required: false,
        default: false,
    },
    activatorId: {
        type: String,
        required: true,
    },
});

const emit = defineEmits(['logout', 'settings']);

const userStore = useUserStore();
const menuOpen = ref(false);
const showSettingsDialog = ref(false);

// Computed properties
const userName = computed(() => {
    if (_.isEmpty(props.user.first_name) || _.isEmpty(props.user.last_name)) {
        return props.user.email.split('@')[0];
    }
    return props.user.first_name + ' ' + props.user.last_name;
});

const userMail = computed(() => props.user.email);

const avatarText = computed(() => {
    if (_.isEmpty(props.user.first_name) || _.isEmpty(props.user.last_name)) {
        return props.user.email.charAt(0).toUpperCase();
    }
    return (
        props.user.first_name.charAt(0).toUpperCase() + props.user.last_name.charAt(0).toUpperCase()
    );
});

const menuItems = computed(() => [
    ...(userStore.is_superuser
        ? [
              {
                  title: 'admin',
                  url: '/admin',
                  icon: ShieldCheckIcon,
                  active: true,
              },
          ]
        : []),
    {
        title: 'settings',
        icon: Settings2Icon,
        active: true,
    },
    {
        title: 'logout',
        icon: LogOutIcon,
        active: true,
    },
]);

// Methods
const handleMenuChange = (isOpen) => {
    menuOpen.value = isOpen;
};

const handleItemClick = (item) => {
    if (item.title === 'logout') {
        menuOpen.value = false;
        emit('logout');
    } else if (item.title === 'settings') {
        menuOpen.value = false;
        showSettingsDialog.value = true;
    }
};

const handleSettingsClose = () => {
    showSettingsDialog.value = false;
    menuOpen.value = false;
};
</script>

<style scoped lang="scss">
.user_name {
    font-size: 13px;
    line-height: 1rem;
}
.email {
    font-size: 10px;
}

.menu-open {
    background: #e0e0e0 !important;
}
</style>
